import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { SubmitMPPOtherDocument } from '@/libs/fleet-api/services/mpp';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Col, Divider, Form, Input, Row, Upload, UploadFile } from 'antd';
import { FC } from 'react';

export const useOtherDocumentModal = () => {
	return useAsyncModal<
		{
			customerId?: string;
			prospectId: string;
		},
		SubmitMPPOtherDocument
	>();
};

const OtherDocumentModal: FC<ReturnType<typeof useOtherDocumentModal>> = ({
	modal,
	data,
	close,
}) => {
	const { form, fieldName } = useForm<SubmitMPPOtherDocument>();
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			{...modal}
			width={940}
			closeIcon={false}
			keyboard={false}
			maskClosable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="CANCEL"
			onCancel={() => close()}
			onOk={() => form.submit()}
			afterOpenChange={(isOpen) => {
				if (!isOpen) form.resetFields();
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Dokumen Tambahan</FleetTitle>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={close}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Nama Dokumen"
							name={fieldName(['documentName'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName(['document'])}
							label="Upload Dokumen"
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName(['document']), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${data!.customerId}/${data!.prospectId}/other-documents`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName(['document']), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName(['document']), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Form.Item
					name={fieldName('notes')}
					label="Keterangan"
					rules={[rule.required()]}
					labelCol={{ span: 5 }}
					wrapperCol={{ span: 19 }}
				>
					<Input.TextArea rows={3} />
				</Form.Item>
			</Form>
		</FleetModal>
	);
};

export default OtherDocumentModal;
